import React, { useState, useEffect, useCallback } from 'react';
import { useToast } from '../contexts/ToastContext';
import apiService from '../../services/apiService';
import { downloadFile } from '../../utils/downloadHelpers';

/**
 * Modal para ver historial de versiones de un documento.
 * Muestra lista cronológica de versiones con metadata y permite descargar cualquiera.
 *
 * @param {Object} props - Propiedades del componente
 * @param {boolean} props.isOpen - Controla si el modal está visible
 * @param {Function} props.onClose - Callback al cerrar, recibe (needsRefresh: boolean)
 * @param {Object} props.documento - Documento cuyo historial mostrar (requiere .id)
 * @returns {JSX.Element|null} Null si isOpen es false
 */
function VersionHistoryModal({ isOpen, onClose, documento }) {
  const { showToast } = useToast();
  const [versiones, setVersiones] = useState([]);
  const [loading, setLoading] = useState(true);

  /**
   * Carga historial de versiones del documento
   * @returns {Promise<void>}
   */
  const fetchVersiones = useCallback(async () => {
    if (!documento?.id) return;
    setLoading(true);
    try {
      const response = await apiService.getVersionesDocumento(documento.id);
      if (response.success) {
        setVersiones(response.data);
      }
    } catch (error) {
      showToast('Error al cargar historial', 'error');
    } finally {
      setLoading(false);
    }
  }, [documento?.id, showToast]);

  useEffect(() => {
    if (isOpen && documento) {
      fetchVersiones();
    }
  }, [isOpen, documento, fetchVersiones]);

  /**
   * Descarga una versión específica del documento
   * @param {number} versionNum - Número de versión a descargar
   * @returns {Promise<void>}
   */
  const handleDownloadVersion = async (versionNum) => {
    try {
      const { blob, filename } = await apiService.downloadVersionDocumento(documento.id, versionNum);
      downloadFile(blob, filename);
      showToast(`Version ${versionNum} descargada`, 'success');
    } catch (error) {
      showToast('Error al descargar version', 'error');
    }
  };

  /**
   * Formatea bytes a formato legible (KB, MB)
   * @param {number} bytes - Tamaño en bytes
   * @returns {string} Tamaño formateado
   */
  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  if (!isOpen) return null;

  return (
    <div className="modal-backdrop" onClick={() => onClose(false)}>
      <div className="modal-container" style={{ maxWidth: '800px' }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title">Historial de Versiones</h3>
          <button className="modal-close-btn" onClick={() => onClose(false)}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="modal-body" style={{ maxHeight: '500px', overflowY: 'auto' }}>
          {loading ? (
            <div className="text-center">
              <i className="fas fa-spinner fa-spin"></i> Cargando historial...
            </div>
          ) : versiones.length === 0 ? (
            <div className="text-center text-muted">
              No hay versiones disponibles
            </div>
          ) : (
            <div className="version-history-list">
              {versiones.map(v => (
                <div key={v.id} className={`version-item ${v.es_vigente ? 'version-vigente' : ''}`}>
                  <div className="version-header">
                    <div>
                      <strong>Version {v.version}</strong>
                      {!!v.es_vigente && <span className="badge badge-success" style={{ marginLeft: 'var(--space-sm)' }}>VIGENTE</span>}
                    </div>
                    <span className="text-muted">
                      {new Date(v.created_at).toLocaleString('es-CL', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                  </div>

                  <p className="version-filename">{v.filename}</p>

                  {v.notas && <p className="version-notas"><em>{v.notas}</em></p>}

                  <div className="version-meta">
                    <span><i className="fas fa-user"></i> {v.created_by || 'Sistema'}</span>
                    {v.tamano_bytes && (
                      <span><i className="fas fa-file"></i> {formatFileSize(v.tamano_bytes)}</span>
                    )}
                    {v.fecha_vencimiento && (
                      <span>
                        <i className="fas fa-calendar"></i> Vence: {new Date(v.fecha_vencimiento).toLocaleDateString('es-CL')}
                      </span>
                    )}
                  </div>

                  <div className="version-actions">
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={() => handleDownloadVersion(v.version)}
                    >
                      <i className="fas fa-download"></i> Descargar
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default VersionHistoryModal;
